import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAPIErrorMessage } from '../../../../helpers/helpers';
import { AuthService } from '../../../../service/AuthService';
import { FlowService } from '../../../../service/FlowService';
import { useAuthStore } from '../../../../store/storeAuth';
import useFlowsStore from '../../../../store/storeFlows';
import {
  NotificationType,
  useNotificationStore,
} from '../../../../store/storeNotifications';
import { ensureReservedFolder } from '../util/ensureReservedFolder';

interface FluxbotQuickNameDialogProps {
  open: boolean;
  spaceId?: string;
  onClose: () => void;
}

export const FluxbotQuickNameDialog: React.FC<FluxbotQuickNameDialogProps> = ({
  open,
  spaceId,
  onClose,
}) => {
  const navigate = useNavigate();
  const setAuthUser = useAuthStore(state => state.setAuthUser);
  const setFolders = useAuthStore(state => state.setFolders);
  const addFlow = useFlowsStore(state => state.addFlow);
  const setNotification = useNotificationStore(state => state.setNotification);
  const [name, setName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (!open) {
      setName('');
      setIsCreating(false);
    }
  }, [open]);

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setNotification({
        type: NotificationType.Error,
        message: 'Please enter a name',
      });
      return;
    }
    setIsCreating(true);
    try {
      const folderId = await ensureReservedFolder('Fluxbots', spaceId, {
        onPrivateFoldersUpdated: privateFolders => setFolders(privateFolders),
      });
      if (!folderId) {
        setNotification({
          type: NotificationType.Error,
          message: 'Failed to create folder Fluxbots',
        });
        return;
      }
      const response = await FlowService.createFlow({
        name: trimmed,
        folderId,
        type: 'chat',
      });
      addFlow(response.data);
      const userRes = await AuthService.getMe();
      setAuthUser(userRes?.data);
      window.dispatchEvent(new CustomEvent('sidebar-refresh'));
      setNotification({
        type: NotificationType.Success,
        message: 'Fluxbot created successfully',
      });
      onClose();
      await new Promise(resolve => setTimeout(resolve, 100));
      navigate(`/dashboard-beta/agent/${response.data.id}`);
    } catch (error) {
      setNotification({
        type: NotificationType.Error,
        message: getAPIErrorMessage(error),
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='xs'>
      <DialogTitle>New Fluxbot</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <TextField
            autoFocus
            fullWidth
            label='Name'
            value={name}
            disabled={isCreating}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleCreate();
              }
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isCreating}>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleCreate}
          disabled={isCreating || !name.trim()}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};
